import React from 'react';
import Matter from '../../../common/model/matter/Matter';
import HwzComponent from '../../../common/component/HwzComponent';
import './ShareMatterPanel.less';
import StringUtil from '../../../common/util/StringUtil';
import DateUtil from '../../../common/util/DateUtil';
import Expanding from '../../widget/Expanding';
import {
  DownloadOutlined,
  EllipsisOutlined,
  UnlockOutlined,
  EyeOutlined,
  FolderOutlined,
} from '@ant-design/icons';
import { Tooltip, Button, Dropdown, Menu } from 'antd';
import SafeUtil from '../../../common/util/SafeUtil';
import Lang from '../../../common/model/global/Lang';
import Share from '../../../common/model/share/Share';
import ImageUtil from '../../../common/util/ImageUtil';
import Moon from '../../../common/model/global/Moon';
import MessageBoxUtil from '../../../common/util/MessageBoxUtil';
import PreviewerHelper from '../../../pages/widget/previewer/PreviewerHelper';
import DownloadToken from '../../../common/model/download/token/DownloadToken';
import PreviewEngine from '../../../common/model/preference/model/PreviewEngine';

interface IProps {
  matter: Matter;
  share: Share;
  currentShareRootUuid: string;
  onPreviewImage: (matter: Matter) => any;
  onGoToDirectory: (id: string) => any;
}

interface IState {}

export default class ShareMatterPanel extends HwzComponent<IProps, IState> {
  // 小屏幕下操作栏
  showMore: boolean = false;

  constructor(props: IProps) {
    super(props);
  }

  isShareOwner(): boolean {
    const { share } = this.props;
    const currentUser = Moon.getSingleton().user;
    return Boolean(currentUser.uuid && share.userUuid && currentUser.uuid === share.userUuid);
  }

  private isPrivateShare(): boolean {
    const { share } = this.props;
    return share.shareScope === 'PRIVATE';
  }

  isPublicShare(): boolean {
    const { share } = this.props;
    return share.shareScope === 'PUBLIC';
  }

  private safeSplitString(str: any, delimiter: string = ','): string[] {
    if (!str) return [];
    try {
      if (typeof str === 'string') {
        return str.split(delimiter).map(item => item.trim()).filter(item => item !== '');
      } else if (Array.isArray(str)) {
        return str.map(item => String(item).trim()).filter(item => item !== '');
      } else {
        return [String(str).trim()].filter(item => item !== '');
      }
    } catch (error) {
      console.warn('分割字符串时出错:', error);
      return [];
    }
  }

  private hasPrivateShareAccess(): boolean {
    const { share } = this.props;
    const currentUser = Moon.getSingleton().user;
    console.log('🔐 hasPrivateShareAccess 详细检查:', { currentUser, share });
    if (!currentUser?.uuid) {
      console.log('🔐 无用户信息，无法验证私有分享权限');
      return false;
    }
    if (this.isShareOwner()) {
      console.log('🔐 用户是分享所有者');
      return true;
    }
    if (share.targetUserUuids) {
      const targetUuids = this.safeSplitString(share.targetUserUuids);
      if (targetUuids.includes(currentUser.uuid)) {
        console.log('🔐 用户在目标用户列表中');
        return true;
      }
    }
    if (share.code) {
      console.log('🔐 用户通过分享码验证');
      return true;
    }
    console.log('🔐 用户无私有分享访问权限');
    return false;
  }
  
  private isOfficeFile(matter: Matter): boolean {
    const extension = matter.name.split('.').pop()?.toLowerCase();
    const officeExtensions = ['doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx', 'pptm'];
    const isOffice = officeExtensions.includes(extension || '');
    console.log('📄 Office文件详细检查:', matter.name, extension, isOffice);
    return isOffice;
  }

  private canUseAlienPreview(matter: Matter): boolean {
    const isOffice = this.isOfficeFile(matter);
    const isPrivate = this.isPrivateShare();
    const currentUser = Moon.getSingleton().user;
    console.log('🔗 canUseAlienPreview 详细检查:', { isOffice, isPrivate, currentUser });
    return isOffice && isPrivate;
  }

  hasAccessPermission(): boolean {
    const { share } = this.props;
    const currentUser = Moon.getSingleton().user;
    console.log('🔐 hasAccessPermission 检查:', { currentUser, share });
    if (share.code) {
      console.log('🔐 通过分享码验证');
      return true;
    }
    if (this.isShareOwner()) {
      console.log('🔐 用户是分享所有者');
      return true;
    }
    if (share.shareScope === 'PUBLIC') {
      console.log('🔐 公开分享，有权限');
      return true;
    }
    if (share.shareScope === 'PRIVATE' && share.targetUserUuids && currentUser.uuid) {
      const targetUuids = this.safeSplitString(share.targetUserUuids);
      if (targetUuids.includes(currentUser.uuid)) {
        console.log('🔐 用户在目标用户列表中');
        return true;
      }
    }
    console.log('🔐 无访问权限');
    return false;
  }

  hasDownloadPermission(): boolean {
    const { share } = this.props;
    if (this.isShareOwner()) {
      return true;
    }
    return share.sharePermission !== 'PREVIEW_ONLY';
  }

  clickRow() {
    if (!this.hasAccessPermission()) {
      MessageBoxUtil.error(Lang.t('share.noPermission'));
      return;
    }
    const { matter, onGoToDirectory, onPreviewImage } = this.props;
    if (matter.dir) {
      onGoToDirectory(matter.uuid!);
    } else {
      if (matter.isImage()) {
        onPreviewImage(matter);
      } else {
        this.previewFile();
      }
    }
  }

  getIcon() {
    const { matter, share, currentShareRootUuid } = this.props;
    if (matter.isImage()) {
      return ImageUtil.handleImageUrl(
        this.getPreviewUrl(),
        false,
        100,
        100
      );
    } else {
      return matter.getIcon();
    }
  }

  getPreviewUrl(): string {
    const { matter, share, currentShareRootUuid } = this.props;
    return matter.getSharePreviewUrl(
      share.uuid!,
      share.code!,
      currentShareRootUuid
    );
  }

  getDownloadUrl(): string {
    const { matter, share, currentShareRootUuid } = this.props;
    return matter.getShareDownloadUrl(
      share.uuid!,
      share.code!,
      currentShareRootUuid
    );
  }

  private generateDirectPreviewUrl(): Promise<string> {
    return new Promise((resolve, reject) => {
      const { matter, share } = this.props;
      const currentUser = Moon.getSingleton().user;
      const isShareOwner = this.isShareOwner();
      console.log('🔗 生成直接预览URL，文件:', matter.name);
      if (matter.privacy && !isShareOwner) {
        let url = matter.getPreviewUrl();
        if (url.indexOf('?') >= 0) {
          url += `&shareUuid=${share.uuid}`;
        } else {
          url += `?shareUuid=${share.uuid}`;
        }
        if (share.code) {
          url += `&shareCode=${share.code}`;
        }
        resolve(url);
      } else if (matter.privacy) {
        const downloadToken = new DownloadToken();
        downloadToken.httpFetchDownloadToken(matter.uuid!,
          () => {
            let url = matter.getPreviewUrl(downloadToken.uuid!);
            if (url.indexOf('?') >= 0) {
              url += `&shareUuid=${share.uuid}`;
            } else {
              url += `?shareUuid=${share.uuid}`;
            }
            resolve(url);
          },
          () => {
            reject(new Error('获取下载token失败'));
          }
        );
      } else {
        let url = matter.getPreviewUrl();
        if (url.indexOf('?') >= 0) {
          url += `&shareUuid=${share.uuid}`;
        } else {
          url += `?shareUuid=${share.uuid}`;
        }
        resolve(url);
      }
    });
  }

  download() {
    if (!this.hasAccessPermission()) {
      MessageBoxUtil.error(Lang.t('share.noPermission'));
      return;
    }
    if (!this.hasDownloadPermission()) {
      MessageBoxUtil.warning(Lang.t('share.previewOnlyNoDownload'));
      return;
    }
    const { matter } = this.props;
    const downloadUrl = this.getDownloadUrl();
    Matter.downloadZip(matter.uuid!, matter.spaceUuid!, undefined, downloadUrl);
  }

/**
 * 获取分享预览 token（保持不变）
 */
private fetchSharePreviewToken(
  shareUuid: string, 
  shareCode: string, 
  matterUuid: string,
  shareRootUuid: string  // 新增参数
): Promise<string> {
  return new Promise((resolve, reject) => {
    const params = new URLSearchParams({
      shareUuid,
      shareCode,
      matterUuid,
      shareRootUuid,      // 传递根节点UUID
    });

    fetch(`/api/share/fetch/preview/token?${params.toString()}`, {
      credentials: 'include'
    })
      .then(res => res.json())
      .then(result => {
        if (result.code === 'OK' && result.data && result.data.token) {
          resolve(result.data.token);
        } else {
          reject(new Error(result.msg || '获取预览 token 失败'));
        }
      })
      .catch(err => reject(err));
  });
}
// 在 ShareMatterPanel.tsx 中找到 previewFile 方法，替换其中的 Office 公开分享分支

// 在 ShareMatterPanel.tsx 中找到 previewFile 方法，替换为以下代码
previewFile = async () => {
  const { matter, onPreviewImage, share, currentShareRootUuid } = this.props; // 确保取到 currentShareRootUuid
  const currentUser = Moon.getSingleton().user;

  console.log('🔗 开始预览文件流程:', matter.name, share.shareScope);

  if (!this.hasAccessPermission()) {
    MessageBoxUtil.error(Lang.t('share.noPermission'));
    return;
  }

  if (matter.dir) {
    this.props.onGoToDirectory(matter.uuid!);
    return;
  }

  if (matter.isImage()) {
    onPreviewImage(matter);
    return;
  }

  try {
    const isOffice = this.isOfficeFile(matter);

    // 针对公开分享的 Office 文件采用独立的 token 预览流程（模仿移动盘）
    if (isOffice && share.shareScope === 'PUBLIC') {
      console.log('🔗 Office文件公开分享，使用独立 token 预览接口（带文件名）');
      try {
        const token = await this.fetchSharePreviewToken(
          share.uuid!,
          share.code!,
          matter.uuid!,
          currentShareRootUuid || ''   // 重点：传入第四个参数
        );

        const encodedFilename = encodeURIComponent(matter.name);
        const sharePreviewPath = `/api/share/token/preview/${token}/${encodedFilename}`;
        const absoluteUrl = `${window.location.origin}${sharePreviewPath}`;
        const base64Url = PreviewerHelper.b64EncodeUnicode(absoluteUrl);

        let kkFileViewBase = '';
        const moon = Moon.getSingleton();
        const previewConfig = moon.preference?.previewConfig;

        if (previewConfig && previewConfig.previewEngines) {
          const kkEngine = previewConfig.previewEngines.find((engine: PreviewEngine) => 
            engine && engine.url && engine.url.includes('/onlinePreview')
          );
          if (kkEngine) {
            try {
              const urlObj = new URL(kkEngine.url);
              kkFileViewBase = urlObj.origin;
              console.log('🔧 从预览引擎获取 kkFileView 地址:', kkFileViewBase);
            } catch (e) {
              console.warn('解析预览引擎 URL 失败，尝试正则提取', e);
              const match = kkEngine.url.match(/^(https?:\/\/[^\/]+)/);
              if (match) kkFileViewBase = match[1];
            }
          }
        }

        if (!kkFileViewBase) {
          console.warn('⚠️ 未找到 kkFileView 预览引擎，将基于当前地址生成默认地址');
          const currentOrigin = window.location.origin;
          try {
            const url = new URL(currentOrigin);
            url.port = '8012';
            kkFileViewBase = url.origin;
          } catch (e) {
            if (currentOrigin.startsWith('http')) {
              kkFileViewBase = currentOrigin.replace(/:\d+/, ':8012');
            } else {
              kkFileViewBase = 'http://' + window.location.hostname + ':8012';
            }
          }
          MessageBoxUtil.warning(`未配置预览服务，尝试使用默认地址 ${kkFileViewBase}，若无法预览请检查配置`);
        }

        const kkViewUrl = `${kkFileViewBase}/onlinePreview?url=${encodeURIComponent(base64Url)}&fullfilename=${encodedFilename}&preview=true&disposition=inline`;
        window.open(kkViewUrl);
      } catch (error) {
        console.error('Token预览失败:', error);
        const errorMessage = error instanceof Error ? error.message : '未知错误';
        MessageBoxUtil.error('预览失败：' + errorMessage);
      }
      return;
    }

    // 其他情况（私有分享、非 Office 文件等）走原有预览逻辑
    if (this.canUseAlienPreview(matter)) {
      const previewUrl = await this.generateDirectPreviewUrl();
      await PreviewerHelper.preview(matter, previewUrl);
    } else {
      const sharePreviewUrl = this.getPreviewUrl();
      await PreviewerHelper.preview(matter, sharePreviewUrl);
    }
  } catch (error) {
    console.error('预览失败:', error);
    const errorMessage = error instanceof Error ? error.message : '未知错误';
    MessageBoxUtil.error(Lang.t('matter.previewFailed') + ': ' + errorMessage);
  }
};

  getMoreMenu = () => {
    const { matter } = this.props;
    const hasDownloadPermission = this.hasDownloadPermission();
    return (
      <Menu>
        <Menu.Item key="download" onClick={this.download} disabled={!hasDownloadPermission}>
          <DownloadOutlined />
          {Lang.t('matter.download')}
          {!hasDownloadPermission && ` (${Lang.t('share.previewOnly')})`}
        </Menu.Item>
        {!matter.dir && (
          <Menu.Item key="preview" onClick={this.previewFile}>
            <EyeOutlined />
            {Lang.t('matter.preview')}
          </Menu.Item>
        )}
      </Menu>
    );
  };

  toggleHandles() {
    this.showMore = !this.showMore;
    this.updateUI();
  }

  render() {
    const { matter, share } = this.props;
    const hasPermission = this.hasAccessPermission();
    const hasDownloadPermission = this.hasDownloadPermission();
    const isShareOwner = this.isShareOwner();
    const isPrivateShare = this.isPrivateShare();
    const canUseAlienPreview = this.canUseAlienPreview(matter);

    return (
      <div className="widget-share-matter-panel">
        <div onClick={(e) => SafeUtil.stopPropagationWrap(e)(this.clickRow())}>
          <div className="media clearfix">
            <div className="pull-left">
              <div className="left-part">
                <span className="cell">
                  <img className="matter-icon" src={this.getIcon()} />
                </span>
              </div>
            </div>

            <div className="pull-right visible-pc">
              <div className="right-part">
                {!matter.dir && (
                  <Tooltip title={!hasPermission ? Lang.t('share.noPermission') : !hasDownloadPermission ? Lang.t('share.previewOnlyNoDownload') : Lang.t('download')}>
                    <Button
                      type="text"
                      icon={<DownloadOutlined />}
                      onClick={(e) => SafeUtil.stopPropagationWrap(e)(this.download())}
                      className="btn-action text-theme"
                      disabled={!hasPermission || !hasDownloadPermission}
                    />
                  </Tooltip>
                )}

                <Tooltip title={matter.dir ? Lang.t('matter.openFolder') : Lang.t('matter.preview')}>
                  <Button
                    type="text"
                    icon={matter.dir ? <FolderOutlined /> : <EyeOutlined />}
                    onClick={(e) => SafeUtil.stopPropagationWrap(e)(this.previewFile())}
                    className="btn-action text-theme"
                    disabled={!hasPermission}
                  />
                </Tooltip>

                <Dropdown overlay={this.getMoreMenu()} trigger={['click']}>
                  <Button type="text" icon={<EllipsisOutlined />} className="btn-action text-theme" disabled={!hasPermission} />
                </Dropdown>

                <Tooltip title={Lang.t('matter.size')}>
                  <span className="matter-size">{StringUtil.humanFileSize(matter.size)}</span>
                </Tooltip>

                <Tooltip title={Lang.t('matter.updateTime')}>
                  <span className="matter-date mr10">{DateUtil.simpleDateHourMinute(matter.updateTime)}</span>
                </Tooltip>
              </div>
            </div>

            <div className="pull-right visible-mobile">
              <span className="more-btn" onClick={(e) => SafeUtil.stopPropagationWrap(e)(this.toggleHandles())}>
                <EllipsisOutlined className="btn-action navy f18" />
              </span>
            </div>

            <div className="media-body">
              <div className="middle-part">
                <span className="matter-name">
                  {matter.name}
                  {!matter.dir && !matter.privacy && (
                    <Tooltip title={Lang.t('matter.publicFileEveryoneCanVisit')}>
                      <UnlockOutlined className="icon" />
                    </Tooltip>
                  )}
                  <span className={`share-type-tag ${isPrivateShare ? 'private' : 'public'}`}>
                    {isPrivateShare ? Lang.t('share.private') : Lang.t('share.public')}
                  </span>
                  {!hasPermission && <span className="no-permission-tag">{Lang.t('share.noPermission')}</span>}
                  {hasPermission && !hasDownloadPermission && <span className="preview-only-tag">{Lang.t('share.previewOnly')}</span>}
                  {canUseAlienPreview && <span className="preview-method-tag">{Lang.t('share.directPreview')}</span>}
                </span>
              </div>
            </div>
          </div>
        </div>

        <Expanding>
          {this.showMore && (
            <div className="more-panel">
              <div className="cell-btn navy text">
                <span>{DateUtil.simpleDateHourMinute(matter.updateTime)}</span>
                <span className="matter-size">{StringUtil.humanFileSize(matter.size)}</span>
              </div>
              <div
                className="cell-btn navy"
                onClick={(e) => SafeUtil.stopPropagationWrap(e)(this.download())}
                style={{ opacity: hasDownloadPermission ? 1 : 0.5, cursor: hasDownloadPermission ? 'pointer' : 'not-allowed' }}
              >
                <DownloadOutlined className="btn-action mr5" />
                {Lang.t('matter.download')}
                {!hasDownloadPermission && ` (${Lang.t('share.previewOnly')})`}
              </div>
              {!matter.dir && (
                <div className="cell-btn navy" onClick={(e) => SafeUtil.stopPropagationWrap(e)(this.previewFile())}>
                  <EyeOutlined className="btn-action mr5" />
                  {Lang.t('matter.preview')}
                  {canUseAlienPreview && ` (${Lang.t('share.directPreview')})`}
                </div>
              )}
            </div>
          )}
        </Expanding>
      </div>
    );
  }
}